import React from 'react';
import { find, isEmpty, isNil, maxBy } from 'lodash';
import ReactDOMServer from 'react-dom/server';

import InnerCircleLabel from './InnerCircleLabel';

const convertToPercentage = val => (val == null ? null : Math.round(val * 100));
const ZERO_VALUE_REPRESENTATION = 0.0001; // Used to still draw a beginning of a bar in the gauge, when value is 0

const getRepresentationValue = val => {
  return !val ? ZERO_VALUE_REPRESENTATION : convertToPercentage(val);
};
export const energySavingChartSeries = (t, energySavingsStore, shouldRestrictEnergySavings = false, token) => [
  {
    name: t('DAY'),
    label: {
      enabled: true,
      format: 'point.value',
    },
    data: [
      {
        color: token.Ecoplant_Charts.colorEnergyDay,
        radius: '90%',
        innerRadius: '80%',
        y: shouldRestrictEnergySavings
          ? energySavingsStore.energySavings?.min?.yearSavings?.specificPowerDeltaRelative ?? ZERO_VALUE_REPRESENTATION
          : getRepresentationValue(energySavingsStore.energySavings.daySavings?.specificPowerDeltaRelative),
      },
    ],
  },
  {
    name: energySavingsStore.isHistoricalData || shouldRestrictEnergySavings ? t('MONTH') : t('MTD'),
    data: [
      {
        color: token.Ecoplant_Charts.colorEnergyMonth,
        radius: '75%',
        innerRadius: '65%',
        y: shouldRestrictEnergySavings
          ? ((energySavingsStore.energySavings?.min?.yearSavings?.specificPowerDeltaRelative ??
              ZERO_VALUE_REPRESENTATION) +
              (energySavingsStore.energySavings?.max?.yearSavings?.specificPowerDeltaRelative ??
                ZERO_VALUE_REPRESENTATION)) /
            2
          : getRepresentationValue(energySavingsStore.energySavings.monthSavings?.specificPowerDeltaRelative),
      },
    ],
  },
  {
    name: energySavingsStore.isHistoricalData || shouldRestrictEnergySavings ? t('YEAR') : t('YTD'),
    data: [
      {
        color: token.Ecoplant_Charts.colorEnergyYear,
        radius: '60%',
        innerRadius: '50%',
        y: shouldRestrictEnergySavings
          ? energySavingsStore.energySavings?.max?.yearSavings?.specificPowerDeltaRelative ?? ZERO_VALUE_REPRESENTATION
          : getRepresentationValue(energySavingsStore.energySavings.yearSavings?.specificPowerDeltaRelative),
      },
    ],
  },
];

export const EnergySavingChartConfig = (
  animation,
  t,
  energySavingsStore,
  shouldRestrictEnergySavings = false,
  token,
) => {
  return {
    chart: {
      animation,
      type: 'solidgauge',
      height: '100%',
      width: 250,
      backgroundColor: token.colorBgContainer,
      style: {
        fontFamily: 'Heebo',
      },
      events: {
        render: function () {
          const chart = this;
          // remove previous existing labels(when switching years, previous labels remain drawn)
          chart.labels?.forEach(label => !isEmpty(label) && label && label.destroy());
          // add series labels
          chart.labels = chart.series.map(function (series) {
            const label = chart.renderer
              .text(
                series.name,
                null,
                chart.plotTop +
                  series.points[0].shapeArgs.y -
                  series.points[0].shapeArgs.r +
                  (series.points[0].shapeArgs.r - series.points[0].shapeArgs.innerR) / 2 +
                  4,
              )
              .attr({
                fill: token.colorText,
              })
              .css({ fontFamily: 'Heebo, sans-serif', fontWeight: 500 })
              .add()
              .toFront();
            return label;
          });

          // align labels to the left
          const longestLabelWidth = maxBy(
            chart.labels,
            (label: { getBBox: () => { width: number } }) => label?.getBBox().width,
          )?.getBBox().width;
          chart.labels?.forEach(label =>
            label.attr({
              x: chart.plotLeft + chart.series[0].points[0].shapeArgs.x - 12 - longestLabelWidth,
            }),
          );
          // add inner circle labels
          const realtimeSeries = find(chart.series, {
            name: t('DAY'),
          });
          if (!realtimeSeries) return;

          const percentage = convertToPercentage(
            energySavingsStore.energySavings.yearSavings?.specificPowerDeltaRelative,
          );
          if (chart.innerLabel) !isEmpty(chart.innerLabel) && chart.innerLabel && chart.innerLabel.destroy();
          chart.innerLabel = chart.renderer
            .html(
              ReactDOMServer.renderToString(
                <InnerCircleLabel
                  percentage={percentage}
                  isHistoricalData={energySavingsStore.isHistoricalData}
                  style={{ transform: 'translate(-50%, -35%)' }}
                  calculatedPercentages={
                    shouldRestrictEnergySavings &&
                    !isNil(energySavingsStore.energySavings?.min?.yearSavings?.specificPowerDeltaRelative) &&
                    !isNil(energySavingsStore.energySavings?.max?.yearSavings?.specificPowerDeltaRelative)
                      ? {
                          min: energySavingsStore.energySavings?.min?.yearSavings?.specificPowerDeltaRelative,
                          max: energySavingsStore.energySavings?.max?.yearSavings?.specificPowerDeltaRelative,
                        }
                      : undefined
                  }
                  token={token}
                />,
              ),
              chart.plotWidth / 2,
              chart.plotHeight / 2,
            )
            .attr({
              fill: token.colorText,
            })
            .add()
            .toFront();
        },
      },
      spacing: 0,
      margin: 0,
    },

    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },

    pane: {
      animation,
      startAngle: 0,
      endAngle: 290,
      background: {
        backgroundColor: token.colorBgContainer,
        borderWidth: 0,
      },
      size: '110%',
    },

    exporting: {
      enabled: false,
    },

    yAxis: {
      min: 0,
      max: 40,
      lineWidth: 0,
      tickPositions: [],

      labels: {
        format: 'XXX',
        y: 25,
        align: 'left',
        style: {
          color: token.colorText,
          fontSize: '20px',
        },
      },
    },
    tooltip: {
      enabled: false,
    },

    plotOptions: {
      solidgauge: {
        animation,
        dataLabels: {
          enabled: false,
          format: 'point.value',
        },
        linecap: 'round',
        stickyTracking: false,
        rounded: true,
        enableMouseTracking: !shouldRestrictEnergySavings,
      },
    },
  };
};
